import type { SketchControlsState } from './sketch-controls-slice';
import sketchControlsReducer, {
    clearBlueprint,
    clearForPastVideoMatch,
    loadFromCookie,
    setBlueprint,
} from './sketch-controls-slice';

import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import Cookies from 'js-cookie';

const COOKIE_NAME = 'sketch_controls';
const COOKIE_EXPIRES_DAYS = 365; // 1 year

// Cookies written before a field existed simply lack it, so every field is
// optional on the way back in.
export type SketchControlsCookieData = Partial<SketchControlsState>;

function writeCookie(controls: SketchControlsState) {
    const { cookiePersistenceEnabled: _, ...controlsToSave } = controls;
    Cookies.set(COOKIE_NAME, JSON.stringify(controlsToSave), { expires: COOKIE_EXPIRES_DAYS });
}

export function saveSketchControlsToCookie() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            const state = getState();
            const controls = state.sketchControls;
            if (controls) {
                if (!controls.cookiePersistenceEnabled) {
                    return;
                }
                writeCookie(controls);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to save sketch controls to cookie:', error);
        }
    };
}

export function loadSketchControlsFromCookie() {
    return (dispatch: AppDispatch) => {
        try {
            const cookieValue = Cookies.get(COOKIE_NAME);
            if (cookieValue) {
                const data = JSON.parse(cookieValue) as SketchControlsCookieData;
                dispatch(loadFromCookie(data));
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to load sketch controls from cookie:', error);
        }
    };
}

/**
 * Picks the blueprint the next video should match, as a setting the chat shows as a pill and
 * sends to the agent with every message, and remembers it for the next visit.
 */
export function matchBlueprint(blueprintId: number) {
    return (dispatch: AppDispatch) => {
        dispatch(setBlueprint(blueprintId));
        dispatch(saveSketchControlsToCookie());
    };
}

/**
 * The same pick, made straight into the saved settings: for a page about to send the user to one
 * that reads them, whose own store may not hold the settings at all.
 */
export function saveBlueprintMatchToCookie(blueprintId: number): void {
    try {
        const cookieValue = Cookies.get(COOKIE_NAME);
        const saved = cookieValue ? (JSON.parse(cookieValue) as SketchControlsCookieData) : {};
        const loaded = sketchControlsReducer(undefined, loadFromCookie(saved));
        writeCookie(sketchControlsReducer(loaded, setBlueprint(blueprintId)));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save sketch controls to cookie:', error);
    }
}

/** Starts the saved design settings over for a past video picked to match, which decides them. */
export function clearSettingsForPastVideoMatch() {
    return (dispatch: AppDispatch) => {
        dispatch(clearForPastVideoMatch());
        dispatch(saveSketchControlsToCookie());
    };
}

export function clearMatchedBlueprint() {
    return (dispatch: AppDispatch) => {
        dispatch(clearBlueprint());
        dispatch(saveSketchControlsToCookie());
    };
}
